import React, { ReactNode, createContext, useContext, useState } from "react";

interface LoadingContextType {
  loadingCount: number;
  loading: {
    isLoading: () => boolean;
    show: () => void;
    hide: () => void;
    reset: () => void;
    count: () => number;
  };
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }

  const executeWithLoading = async <T,>(action: () => Promise<T>) => {
    context.loading.show();
    try {
      return await action();
    } finally {
      context.loading.hide();
    }
  };

  return { loading: context.loading, executeWithLoading };
};

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loadingCount, setLoadingCount] = useState(0);

  const loading = {
    isLoading: () => loadingCount > 0,
    show: () => setLoadingCount((prev) => prev + 1),
    hide: () => setLoadingCount((prev) => (prev === 0 ? 0 : prev - 1)),
    reset: () => setLoadingCount(0),
    count: () => loadingCount,
  };

  return <LoadingContext.Provider value={{ loadingCount, loading }}>{children}</LoadingContext.Provider>;
};
